import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IconPlusCircle } from "../../../icons/icons";
import { cleanSymbol, shortName, instrumentName, formatPrice, formatDayChange, dayChangeColor } from "../watchlistFormat";
import { stockSearch, getWatchlistItems } from "../../../api/user";
import SegmentedAssetSearch from "../SegmentedAssetSearch";
import AssetAvatar from "../AssetAvatar";
import ListSkeleton from "../../Skeleton/ListSkeleton";

// Screen 5 — Add Assets. Title has no list name (mobile drops it for space).
// Browse the default category list (getWatchlistItems) + search (stockSearch).
const CATEGORY_KEY = { Stocks: "stocks", Crypto: "crypto", Forex: "forex" };
const BROWSE_LIMIT = 3; // small pages load fast (each row is price-enriched); "Load more" fetches 3 more

export default function MobileAddAssets({ listName, onClose, onAdd }) {
  const [category, setCategory] = useState("Stocks");
  const [query, setQuery] = useState("");

  const [browseItems, setBrowseItems] = useState([]);
  const [browsePage, setBrowsePage] = useState(1);
  const [browseHasMore, setBrowseHasMore] = useState(false);
  const [browseLoading, setBrowseLoading] = useState(false);

  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const controllerRef = useRef(null);
  const debounceRef = useRef(null);
  const browseReqRef = useRef(0);

  const trimmedQuery = query.trim();
  const isSearchMode = trimmedQuery.length > 0;

  const loadBrowse = useCallback(async (cat, page) => {
    const key = CATEGORY_KEY[cat] || "stocks";
    const reqId = ++browseReqRef.current;
    setBrowseLoading(true);
    try {
      const r = await getWatchlistItems(key, page, BROWSE_LIMIT);
      if (reqId !== browseReqRef.current) return;
      const its = Array.isArray(r?.data?.items) ? r.data.items : [];
      setBrowseItems((prev) => (page === 1 ? its : [...prev, ...its]));
      setBrowsePage(page);
      setBrowseHasMore(r?.data?.pagination?.has_more ?? its.length === BROWSE_LIMIT);
    } catch (e) {
      if (reqId === browseReqRef.current) {
        console.error("Failed to load assets", e);
        if (page === 1) setBrowseItems([]);
      }
    } finally {
      if (reqId === browseReqRef.current) setBrowseLoading(false);
    }
  }, []);

  useEffect(() => {
    setBrowseItems([]);
    setBrowsePage(1);
    setBrowseHasMore(false);
    loadBrowse(category, 1);
  }, [category, loadBrowse]);

  useEffect(() => {
    if (!trimmedQuery) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Mark the search busy from the FIRST keystroke, not when the debounce
    // fires — otherwise those 400ms render as "No matches found".
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      if (controllerRef.current) controllerRef.current.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const res = await stockSearch({ category, query: trimmedQuery }, controller.signal);
        setSearchResults(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (err?.name !== "CanceledError") {
          console.error("Asset search failed:", err);
          setSearchResults([]);
        }
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [trimmedQuery, category]);

  const navigate = useNavigate();
  // Clicking the asset (avatar + name) opens its chart, mirroring the watchlist
  // AssetRow: stash the ticker and route to /chartSearch/:symbol. The + button
  // still adds the asset to the list.
  const openChart = (sym) => {
    if (!sym) return;
    localStorage.setItem("ticker", sym);
    navigate(`/chartSearch/${sym}`);
  };

  const displayFor = (item) => {
    const catKey = CATEGORY_KEY[category] || "stocks";
    if (item.ticker) {
      // Search-result shape. Chip is the BASE symbol only ("X:BTCUSD" -> "BTC");
      // title is the title-cased server name.
      const chip = cleanSymbol(item.ticker, catKey);
      return {
        chip,
        // No brand art exists for provider search hits — the chip stands in.
        logo: null,
        name: instrumentName({ name: item.name, symbol: item.ticker, category: catKey }),
        addSymbol: item.ticker,
        price: null,
        change: null,
        raw: null,
      };
    }
    const chip = cleanSymbol(item.symbol);
    const name = item.details?.stock_name ? shortName(item.details.stock_name) : item.watchlist_name || chip;
    return {
      chip,
      // Curated rows carry hand-picked brand art; prefer it over the initials.
      logo: item.logo || null,
      name,
      addSymbol: item.details?.stock_symbol || item.symbol,
      price: formatPrice(item.details?.latestData?.c),
      change: formatDayChange(item.details?.percentageChange),
      raw: item.details?.percentageChange,
    };
  };

  const rows = isSearchMode ? searchResults : browseItems;
  const loading = isSearchMode ? searching : browseLoading;

  return (
    <div className="pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <h1 className="font-montserrat text-title-subpage font-bold text-[#004d4d]">Add Assets</h1>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 text-sm font-medium text-secondary hover:text-primaryColor"
        >
          Close
        </button>
      </div>
      <p className="mt-1 text-sm text-secondary">Search and add assets to this list</p>

      <div className="mt-4">
        <SegmentedAssetSearch
          category={category}
          onCategoryChange={setCategory}
          query={query}
          onQueryChange={setQuery}
          // "Searching" belongs to an actual search — a browse load is announced
          // by the skeleton rows below, not by a word in the empty search field.
          loading={isSearchMode && searching}
        />
      </div>

      {/* Rows */}
      <div className="mt-3">
        {rows.map((item, i) => {
          const d = displayFor(item);
          return (
            <div key={`${d.addSymbol}-${i}`} className="flex items-center gap-3 border-b border-gray-100 py-2.5">
              <button
                type="button"
                onClick={() => openChart(d.addSymbol)}
                aria-label={`Open ${d.chip} chart`}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <AssetAvatar
                  logo={d.logo}
                  chip={d.chip}
                  className="h-9 w-9"
                  chipTextClass="text-[9px]"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-montserrat text-[15px] text-black" title={d.name}>
                    {d.name}
                  </div>
                  {(d.price || d.change) && (
                    <div className="flex items-center gap-2 text-xs">
                      {d.price && <span className="text-secondary">{d.price}</span>}
                      {d.change && (
                        <span className="font-semibold" style={{ color: dayChangeColor(d.raw) }}>
                          {d.change}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              <button
                type="button"
                aria-label={`Add ${d.chip}`}
                onClick={() => onAdd(d.addSymbol)}
                className="shrink-0 grayscale transition hover:grayscale-0"
              >
                {IconPlusCircle}
              </button>
            </div>
          );
        })}

        {/* First load / fresh search — placeholder rows on the same rhythm as
            the real ones, never an empty screen. */}
        {loading && rows.length === 0 && (
          <ListSkeleton
            rows={BROWSE_LIMIT}
            avatarSize={36}
            columns={0}
            divided={false}
            rowClassName="py-2.5"
            label={isSearchMode ? "Searching" : "Loading assets"}
          />
        )}

        {!loading && rows.length === 0 && (
          <div className="py-12 text-center text-sm text-secondary">
            {isSearchMode ? "No matches found" : "No assets to show"}
          </div>
        )}

        {!isSearchMode && browseHasMore && (
          <div className="flex justify-center py-4">
            <button
              type="button"
              onClick={() => loadBrowse(category, browsePage + 1)}
              disabled={browseLoading}
              className="text-sm font-medium text-primaryColor disabled:opacity-50"
            >
              {browseLoading ? "Loading" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
